// ProfileCardItem.jsx
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { differenceInHours, differenceInMinutes, fromUnixTime } from 'date-fns';

import WithMessageMetaData from '../container/WithMessageMetaData.js';
import MessageVideoPlayer from '../container/MessageVideoPlayer.js';
import MessageThumbnail from '../container/MessageThumbnail.js';
import LinkSubstring from '../container/LinkSubstring.js';
import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import Loading from '../component/Loading.jsx';

import useIsClient from '../hook/useIsClient.js';
import { useSentryEndSpan } from '../hook/useSentrySpan.js';

import { ButtonId } from '../resource/mixpanel.js';
import { Status as UploadStatus } from '../resource/uploadConstants.js';
import { DrmFeatureName } from '../resource/drmConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import formatNumber from '../resource/formatNumber.js';
import withViewportItemTracker from '../resource/withViewportItemTracker.js';
import { EMPTY_ARRAY, NULL_FUNCTION } from '../resource/defaults.js';
import getRatingColor from '../resource/getRatingColor.js';
import { sanitizeFeedQuery } from '../resource/feedUtils.js';

import media from '../style/media.js';
import { color } from '../style/variables.js';
import TextEllipsis from '../style/TextEllipsis.js';
import ViewersIconSrc from '../../img/ic_streaming_view_xs_grey.svg';
import UnlockIconSrc from '../../img/ic_unlock_grey.svg';
import DiamondIconSrc from '../../img/ic_diamond.svg';
import PinIconSrc from '../../img/ic_pin_grey_500.svg';
import ErrorIconSrc from '../../img/ic_error.svg';
import RatingStarActive from '../../img/ic-star-fill.svg';
import RatingStarDefault from '../../img/ic-star-lightgray-fill.svg';
import StarWhiteFillIconSource from '../../img/ic-star-white-fill.svg';

// Constants for rating colors and their corresponding icon sources
const RatingColorIconMap = {
  '#FFD100': RatingStarActive,
  '#FFFFFF': StarWhiteFillIconSource,
  '#9B9B9B': RatingStarDefault,
};

const DEFAULT_RATING_ICON_SOURCE = RatingStarDefault;

export const ProfileCardItem = ({
  messageId = '',
  clipSourceId = '',
  categoryId = '',
  isPinned = false,
  isShorts = false,
  categoryIndex = null,
  clipStartMsec = 0,
  expiresAtUnix = 0,
  originalCaptionText = '',
  uploadJobStatus = null,
  statusI18nKey = '',
  reasonI18nKey = '',
  locationState = null,
  getElementRef = NULL_FUNCTION,
  ratingColorThresholds = EMPTY_ARRAY,
  ratingStepSize = 20,
  isUnlocked = false,
  isFree = false,
  isDrmLicenseAvailabilityFetched = false,
  isDrmLicenseAvailabilityFetching = false,
  fetchDrmLicenseAvailability = NULL_FUNCTION,
}) => {
  const { t } = useTranslation();
  const isClient = useIsClient();
  const { endSpan } = useSentryEndSpan();

  useEffect(() => {
    const canFetch =
      !isDrmLicenseAvailabilityFetched && !isDrmLicenseAvailabilityFetching;
    if (!isFree && canFetch) {
      fetchDrmLicenseAvailability();
    }
  }, [
    isFree,
    fetchDrmLicenseAvailability,
    isDrmLicenseAvailabilityFetched,
    isDrmLicenseAvailabilityFetching,
  ]);

  if (!messageId) {
    return (
      <ProfileCardItemPlaceholder
        getElementRef={getElementRef}
        isShorts={isShorts}
      />
    );
  }

  const renderRemainTime = () => {
    if (!isClient || !expiresAtUnix || isShorts) {
      return null;
    }

    const now = Date.now();
    const remainDate = fromUnixTime(expiresAtUnix);
    const remainMinutes = differenceInMinutes(remainDate, now);

    const remainTime =
      remainMinutes < 0
        ? null
        : 60 > remainMinutes
          ? t('media_timeline_minutes_left', {
              ...[remainMinutes],
              ns: TranslationNamespace.FEED,
            })
          : t('media_timeline_hours_left', {
              ...[differenceInHours(remainDate, now)],
              ns: TranslationNamespace.FEED,
            });
    return (
      remainTime && (
        <TimeLabel>
          <RemainTime>{remainTime}</RemainTime>
        </TimeLabel>
      )
    );
  };

  const isFailed = [
    UploadStatus.UPLOAD_FAILED,
    UploadStatus.PROCESSING_FAILED,
    UploadStatus.REVIEW_FAILED,
    UploadStatus.DELIVERY_FAILED,
    UploadStatus.FAILED,
  ].includes(uploadJobStatus);
  const isReviewing = [
    UploadStatus.REVIEW_STARTED,
    UploadStatus.REVIEW_COMPLETED,
    UploadStatus.DELIVERY_STARTED,
  ].includes(uploadJobStatus);
  const isLocalShorts =
    isShorts &&
    uploadJobStatus != null &&
    UploadStatus.DELIVERY_COMPLETED !== uploadJobStatus;
  const sanitizedCategoryId = sanitizeFeedQuery({ feedName: categoryId });
  const state = Object.keys(locationState || {}).length
    ? locationState
    : {
        listPath: ['home', 'messages', sanitizedCategoryId],
        category: sanitizedCategoryId,
      };
  const trackingPayload = {
    messageId,
    'discover.index': categoryIndex,
    'discover.category': categoryId,
  };
  const props = isShorts
    ? {
        to: `/shorts/${messageId}`,
        state,
        'data-element_id': ButtonId.Profile.ButtonPostClick,
        'data-tracking_payload': trackingPayload,
        onClick: event => {
          if (isLocalShorts) {
            return event.preventDefault();
          }
        },
        $disabled: isLocalShorts,
      }
    : {
        to: `/story/${messageId}${isClient ? location.search : ''}`,
        state,
        'data-element_id': ButtonId.Profile.ButtonPostClick,
        'data-tracking_payload': trackingPayload,
      };

  return (
    <StyledProfileShortCard as={Link} ref={getElementRef} {...props}>
      <MessageCradWrapper isShorts={isShorts}>
        {isLocalShorts ? (
          <MessageVideoPlayer
            messageId={clipSourceId}
            isMuted
            option={{
              autoplay: false,
              style: 'object-fit: cover',
            }}
            shouldUseSplash={false}
            drmFeatureName={DrmFeatureName.FLIX}
            onLoadedMetadata={event => {
              event.target.currentTime = clipStartMsec / 1000;
              event.target.play();
            }}
            onCanplaythrough={event => {
              event.target.pause();
            }}
          />
        ) : (
          <MessageThumbnail
            id={messageId}
            objectFit="cover"
            onLoad={() => endSpan({ category: categoryId })}
          />
        )}
        <Gradient />
        {isLocalShorts && <StatusOverlay />}
        {isLocalShorts && (
          <Status>
            {isFailed ? (
              <ErrorIcon
                src={ErrorIconSrc}
                alt={t(statusI18nKey, {
                  ns: TranslationNamespace.POST,
                })}
              />
            ) : (
              <Loading />
            )}
            <StatusText
              color={
                isFailed
                  ? color.red
                  : isReviewing
                    ? color.sky
                    : color.neutral[100]
              }
            >
              {t(statusI18nKey, {
                ns: TranslationNamespace.POST,
              })}
              {reasonI18nKey
                ? ` :${t(reasonI18nKey, {
                    ns: TranslationNamespace.POST,
                  })}`
                : ''}
            </StatusText>
          </Status>
        )}
        <TopLeftMetadata>
          {isPinned && <PinIcon src={PinIconSrc} alt="pin" />}
        </TopLeftMetadata>
        {renderRemainTime()}
        <WithMessageMetaData messageId={messageId}>
          {({ rating }) => {
            if (!rating) return null;
            const ratingValue = (Number(rating) / ratingStepSize).toFixed(1);
            const { ratingColor } = getRatingColor({
              rating: ratingValue,
              thresholds: ratingColorThresholds,
            });

            return (
              <RatingLabel>
                <RatingIcon ratingColor={ratingColor} alt="rating" />
                <RatingText ratingColor={ratingColor}>{ratingValue}</RatingText>
              </RatingLabel>
            );
          }}
        </WithMessageMetaData>
      </MessageCradWrapper>
      <WithMessageMetaData messageId={messageId}>
        {({ viewCount, unlocks, price }) => {
          return isFree ? (
            <MetadataBottom>
              <Caption>
                <LinkSubstring shouldUseLink={false}>
                  {originalCaptionText}
                </LinkSubstring>
              </Caption>
              <MetadataBottomContent>
                <LeftBottom>
                  <LeftBottomIcon
                    src={unlocks ? UnlockIconSrc : ViewersIconSrc}
                    alt={unlocks ? 'unlocks' : 'view'}
                  />
                  <LeftBottomText>
                    {unlocks ? formatNumber(unlocks) : viewCount}
                  </LeftBottomText>
                </LeftBottom>
                <RightBottom>
                  {price ? (
                    <>
                      <RightBottomIcon src={DiamondIconSrc} alt="diamond" />
                      {price}
                    </>
                  ) : (
                    t('free', {
                      ns: TranslationNamespace.FEED,
                    })
                  )}
                </RightBottom>
              </MetadataBottomContent>
            </MetadataBottom>
          ) : (
            <MetadataBottom>
              <Caption>
                <LinkSubstring shouldUseLink={false}>
                  {originalCaptionText}
                </LinkSubstring>
              </Caption>
              <MetadataBottomContent>
                <LeftBottom>
                  <LeftBottomIcon src={UnlockIconSrc} alt="unlocks" />
                  <LeftBottomText>
                    {unlocks ? formatNumber(unlocks) : '-'}
                  </LeftBottomText>
                </LeftBottom>
                <RightBottom>
                  {isUnlocked ? (
                    <RightBottomRegularText>
                      {t('content_unlocked', {
                        ns: TranslationNamespace.FEED,
                      })}
                    </RightBottomRegularText>
                  ) : (
                    <>
                      <RightBottomIcon src={DiamondIconSrc} alt="diamond" />
                      {price}
                    </>
                  )}
                </RightBottom>
              </MetadataBottomContent>
            </MetadataBottom>
          );
        }}
      </WithMessageMetaData>
    </StyledProfileShortCard>
  );
};

ProfileCardItem.propTypes = {
  messageId: PropTypes.string,
  clipSourceId: PropTypes.string,
  categoryId: PropTypes.string,
  originalTitle: PropTypes.string,
  originalCaptionText: PropTypes.string,
  uploadJobStatus: PropTypes.string,
  mediaType: PropTypes.string,
  statusI18nKey: PropTypes.string,
  reasonI18nKey: PropTypes.string,
  categoryIndex: PropTypes.number,
  clipStartMsec: PropTypes.number,
  expiresAtUnix: PropTypes.number,
  isPinned: PropTypes.bool,
  isShorts: PropTypes.bool,
  locationState: PropTypes.object,
  getElementRef: PropTypes.func,
  ratingColorThresholds: PropTypes.arrayOf(PropTypes.string),
  ratingStepSize: PropTypes.number,
  fetchDrmLicenseAvailability: PropTypes.func,
  isUnlocked: PropTypes.bool,
  isFree: PropTypes.bool,
  isDrmLicenseAvailabilityFetched: PropTypes.bool,
  isDrmLicenseAvailabilityFetching: PropTypes.bool,
};

export const ProfileCardItemPlaceholder = ({ getElementRef, isShorts }) => (
  <StyledProfileShortCard ref={getElementRef} isShorts={isShorts}>
    <MessageCradWrapper isShorts={isShorts}>
      <Gradient />
    </MessageCradWrapper>
    <MetadataBottom></MetadataBottom>
  </StyledProfileShortCard>
);

ProfileCardItemPlaceholder.propTypes = {
  getElementRef: PropTypes.func,
  isShorts: PropTypes.bool,
};

const StyledProfileShortCard = styled.div.attrs(({ $disabled }) => ({
  style: {
    cursor: $disabled ? 'not-allowed' : 'pointer',
  },
}))`
  border-radius: 8px;
  overflow: hidden;
  display: block;
  width: 100%;
`;

const MessageCradWrapper = styled.div.attrs(({ isShorts }) => ({
  style: {
    aspectRatio: isShorts ? 0.56 : 0.66,
  },
}))`
  position: relative;
  > picture {
    display: block;
    width: 100%;
    height: 100%;
  }
`;

const Gradient = styled.div`
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3),
    rgba(0, 0, 0, 0.05) 17%,
    rgba(0, 0, 0, 0.3) 53%,
    rgba(0, 0, 0, 0.5)
  );
  pointer-events: none;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const StatusOverlay = styled.div`
  position: absolute;
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  background-color: rgba(0, 0, 0, 0.5);
`;

const MetadataBottom = styled.div`
  background-color: ${color.grey[900]};
  display: flex;
  flex-direction: column;
  padding: 8px;
  min-height: 58px;
  ${media.tablet`
    min-height: 54px;
  `};
`;

const MetadataBottomContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
`;

const Status = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  padding: 16px;
  width: 100%;
  text-align: center;
  transform: translate(-50%, -50%);
`;

const ErrorIcon = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
`;

const StatusText = styled.div.attrs(({ color }) => ({
  style: {
    color,
  },
}))`
  margin-top: 4px;
  font-size: 12px;
`;

const Caption = styled.div`
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
  line-height: 18px;
  min-height: 18px;

  ${media.tablet`
    font-size: 12px;
    letter-spacing: 0.048px;
  `};
`;

const TimeLabel = styled.div`
  display: inline-block;
  background-color: ${color.lovense + 'cc'}; // add the 20% opacity to the color
  color: white;
  font-size: 14px;
  padding: 2px 8px;
  border-radius: 0 0 0 8px;
  position: absolute;
  top: 0px;
  right: 0px;
  max-width: 112px;
`;

const RatingLabel = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  ${media.tablet`
    height: 18px;
    font-size: 12px;
  `};
  position: absolute;
  right: 4px;
  bottom: 4px;
  background-color: ${color.black}CC;
  border-radius: 4px;
  padding: 4px;
  gap: 2px;
`;

const RatingIcon = styled.img.attrs(({ ratingColor }) => {
  const iconSource =
    RatingColorIconMap[ratingColor] || DEFAULT_RATING_ICON_SOURCE;

  return {
    src: iconSource,
  };
})`
  width: 16px;
  height: 16px;
  ${media.tablet`
    width: 12px;
    height: 12px;
  `};
`;

const RatingText = styled.span.attrs(({ ratingColor }) => {
  return {
    style: {
      color: ratingColor,
    },
  };
})`
  font-weight: 900;
  font-size: 16px;
  ${media.tablet`
    font-size: 12px;
  `};
`;

const TopLeftMetadata = styled.div`
  position: absolute;
  top: 11px;
  left: 6px;
  display: flex;
`;

const PinIcon = styled.img`
  width: 24px;
  height: 24px;
  ${media.mobile`
    width: 16px;
    height: 16px;
  `};
  margin-right: 4px;
`;

const RemainTime = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  letter-spacing: 0.014px;
  ${media.tablet`
    font-size: 12px;
  `};
`;

const LeftBottom = styled.div`
  margin-right: 4px;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 400;
  color: ${color.grey[700]};
  ${media.tablet`
    font-size: 12px;
  `};
`;

const LeftBottomIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 2px;
  object-fit: contain;
  ${media.tablet`
    width: 16px;
    height: 16px;
  `};
`;

const RightBottom = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  ${media.tablet`
    font-size: 12px;
  `};
  font-weight: 600;
  letter-spacing: 0.048px;
  color: ${color.tealBlue};
  overflow: hidden;
`;

const LeftBottomText = styled.div`
  ${TextEllipsis};
  color: ${color.grey[700]};
`;
const RightBottomRegularText = styled.div`
  ${TextEllipsis};
  color: ${color.grey[700]};
  font-weight: 400;
`;

const RightBottomIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 2px;
  object-fit: contain;
  ${media.tablet`
    width: 16px;
    height: 16px;
  `};
`;

export default withViewportItemTracker(
  React.forwardRef((props, ref) => (
    <ProfileCardItem getElementRef={ref} {...props} />
  ))
);
